import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AppBskyFeedDefs } from '@atproto/api'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/hooks/useToast'
import {
    fetchFeedGeneratorDetail,
    sendFeedInteraction,
    isFeedGeneratorFeedUri,
} from '@/services/feedsService'
import { strings } from '@/strings'
import { DISCOVER_FEED_URI } from '@/constants/api'
import type { Post } from '@/types/posts'

export interface FeedTuning {
    busy: boolean
    onShowMore: () => void
    onShowLess: () => void
}

/**
 * Eligibility + acceptsInteractions gating for the "Show more/less like this"
 * post overflow menu actions. Returns null unless the post was fetched from a
 * feed generator whose metadata confirms it accepts interactions — hidden
 * while metadata is loading or errored, never a disabled placeholder.
 *
 * Discover (`DISCOVER_FEED_URI`) is special-cased as always-eligible once its
 * metadata loads, matching the Bluesky reference client (social-app
 * src/state/feed-feedback.tsx) — Discover's generator view never sets
 * `acceptsInteractions`, but the reference client still shows feedback
 * actions on it. Metadata must still load in that case too: the send needs
 * the generator's service DID (`detail.did`) to route the proxied request.
 *
 * Shares the `['feedGenerator', feedUri]` query cache with FeedView by key
 * only (this hook supplies its own queryFn using the current session) — that
 * cache entry is account-agnostic and must only be used for public metadata
 * like `acceptsInteractions`.
 */
export function useFeedTuning(post: Post): FeedTuning | null {
    const { session } = useAuth()
    const { showToast } = useToast()
    const [busy, setBusy] = useState(false)

    const eligible =
        !!session &&
        !post.isActivityPub &&
        post.protocol === undefined &&
        isFeedGeneratorFeedUri(post.fromFeedUri)

    const { data: detail } = useQuery({
        queryKey: ['feedGenerator', post.fromFeedUri],
        queryFn: async () => {
            if (!session) throw new Error('Session unavailable')
            if (!post.fromFeedUri) throw new Error('Missing feed uri')
            return fetchFeedGeneratorDetail(session, post.fromFeedUri)
        },
        enabled: eligible,
        staleTime: 5 * 60_000,
    })

    if (!eligible || !detail) return null
    const isDiscover = post.fromFeedUri === DISCOVER_FEED_URI
    if (!isDiscover && detail.acceptsInteractions !== true) return null

    const send = async (event: string) => {
        if (busy || !session || !post.fromFeedUri) return
        setBusy(true)
        try {
            await sendFeedInteraction(session, {
                feedUri: post.fromFeedUri,
                postUri: post.uri,
                event,
                feedContext: post.feedContext,
                reqId: post.reqId,
                generatorDid: detail.did,
            })
            showToast(strings.posts.feedbackSent, 'success')
        } catch {
            showToast(strings.posts.couldNotSendFeedback, 'error')
        } finally {
            setBusy(false)
        }
    }

    return {
        busy,
        onShowMore: () => void send(AppBskyFeedDefs.REQUESTMORE),
        onShowLess: () => void send(AppBskyFeedDefs.REQUESTLESS),
    }
}
